import React, { FC } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import CustomSelect from "../../../../components/form/CustomSelect";
import InputMask from "react-input-mask";
import { OptionType } from "../../../../types/CommonType";
import { TreatmentModel } from "../../../../models/TreatmentModel";
import { TreatmentMedicationType } from "../../../../types/TreatmentType";
import clsx from "clsx";
import { LANG_COMMON_CANCEL, LANG_COMMON_SAVE } from "../../../../lang";
import { FormattedMessage } from "react-intl";
import { Formik } from "formik";
import * as Yup from "yup";

interface TreatmentMedicationModalProps {
    treatmentMedication: TreatmentMedicationType;
    onSave: (medication: TreatmentMedicationType) => void;
    onCancel: () => void;
}

const TreatmentMedicationModal: FC<TreatmentMedicationModalProps> = ({
    treatmentMedication,
    onSave,
    onCancel
}) => {
    const initialValues: TreatmentMedicationType = treatmentMedication;
    const medicationType = treatmentMedication.type; // used to filter medication options

    const validationSchema = Yup.object().shape({
        medication: Yup.string().required("Campo obrigatório"),
        dosage: Yup.number()
            .typeError("Digite um número")
            .min(0, "Deve ser maior ou igual a 0")
            .required("Campo obrigatório"),
        posology: Yup.string().required("Campo obrigatório"),
        date_start: Yup.date().required("Campo obrigatório"),
        date_end: Yup.date()
            .required("Campo obrigatório")
            .min(Yup.ref('date_start'), 'Data fim deve ser igual ou posterior à data de início'),
        start_time: Yup.string()
            .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Horário inválido')
            .required('Campo obrigatório'),
    });

    return (
        <>
            <Modal
                show={true}
                onHide={onCancel}
                size="lg"
                className="modal-default"
                backdrop="static"
            >
                <Modal.Header
                    closeButton
                    closeVariant="white"
                >
                    <Modal.Title>
                        Medicação
                    </Modal.Title>
                </Modal.Header>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        onSave(values);
                    }}
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, isValid }) => (
                        <>
                            <Modal.Body>
                                <Form as="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                                    <Row>
                                        <Col lg={6}>
                                            <Form.Group className="mb-3" controlId="medication">
                                                <Form.Label>Medicação</Form.Label>
                                                <CustomSelect
                                                    options={TreatmentModel()
                                                        .getMedicationsOptions()
                                                        .filter((o) => o.type === medicationType)
                                                        .map((o) => ({ ...o, label: `${o.label} (${o.unit})` }))}
                                                    key={`medication-${values.medication}`}
                                                    name={`medication`}
                                                    onChange={(o: OptionType) => {
                                                        setFieldValue('medication', o.value);
                                                    }}
                                                    placeholder="Selecione..."
                                                    value={values.medication}
                                                    isSearchable={true}
                                                    isTouched={!!touched.medication}
                                                    isValid={!!touched.medication && !errors.medication}
                                                />
                                                {touched.medication && errors.medication ? (
                                                    <div className="invalid-feedback d-block">{errors.medication}</div>
                                                ) : null}
                                            </Form.Group>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className="mb-3" controlId="dosage">
                                                <Form.Label>Dosagem</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name="dosage"
                                                    value={values.dosage as any}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    min={0}
                                                    isInvalid={!!(touched.dosage && errors.dosage)}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.dosage}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className="mb-3" controlId="posology">
                                                <Form.Label>Posologia</Form.Label>
                                                <CustomSelect
                                                    options={TreatmentModel().getPosologyOptions()}
                                                    name={`posology`}
                                                    onChange={(o: OptionType) => {
                                                        setFieldValue('posology', o.value);
                                                        if (o.value === 'UNI') {
                                                            setFieldValue('date_end', values.date_start);
                                                        }
                                                    }}
                                                    placeholder="Selecione..."
                                                    value={values.posology}
                                                    isTouched={!!touched.posology}
                                                    isValid={!!touched.posology && !errors.posology}
                                                />
                                                {touched.posology && errors.posology ? (
                                                    <div className="invalid-feedback d-block">{errors.posology}</div>
                                                ) : null}
                                            </Form.Group>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className="mb-3" controlId="date_start">
                                                <Form.Label>Data de Início</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="date_start"
                                                    value={values.date_start}
                                                    onChange={(e) => {
                                                        handleChange(e as any);
                                                        if (values.posology === 'UNI') {
                                                            setFieldValue('date_end', (e.target as HTMLInputElement).value);
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                    isInvalid={!!(touched.date_start && errors.date_start)}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.date_start}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className="mb-3" controlId="date_end">
                                                <Form.Label>Data de Fim</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    name="date_end"
                                                    value={values.date_end}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isInvalid={!!(touched.date_end && errors.date_end)}
                                                    min={values.date_start ? values.date_start : undefined}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.date_end}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className="mb-3" controlId="start_time">
                                                <Form.Label>Horário</Form.Label>
                                                <InputMask
                                                    value={values.start_time}
                                                    onChange={(e) => setFieldValue('start_time', e.target.value)}
                                                    name={"start_time"}
                                                    type="text"
                                                    mask="99:99"
                                                    className={clsx("form-control", {
                                                        "is-invalid": !!(touched.start_time && errors.start_time),
                                                    })}
                                                />
                                                {touched.start_time && errors.start_time ? (
                                                    <div className="invalid-feedback d-block">{errors.start_time}</div>
                                                ) : null}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline-light" onClick={onCancel} className="btn-custom btn-custom-light">
                                    <FormattedMessage {...LANG_COMMON_CANCEL} />
                                </Button>
                                <Button
                                    variant="primary"
                                    type={"button"}
                                    className="btn-custom bg-custom-gradient"
                                    onClick={(e) => { e.preventDefault(); handleSubmit(); }}
                                    disabled={!!Object.keys(errors).length}
                                >
                                    <FormattedMessage {...LANG_COMMON_SAVE} />
                                </Button>
                            </Modal.Footer>
                        </>
                    )}
                </Formik>
            </Modal>

        </>
    );
};

export default TreatmentMedicationModal;